import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import {
  Fab,
  Button,
  Card,
  Box,
  Collapse,
  IconButton,
} from "@material-ui/core";

import Typography from "../typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import FitnessCenterIcon from "@material-ui/icons/FitnessCenter";
import DescriptionIcon from "@material-ui/icons/Description";
import AssignmentIcon from "@material-ui/icons/Assignment";
import { gql } from "@apollo/client";
import { styled } from "@material-ui/styles";
import { ActionLink } from "../atoms";
import AddIcon from "@material-ui/icons/Add";
import { injectIntl } from "react-intl";
import messages from "./Exercises.intl";
import { IStyledArguments } from "../../types/styled-arguments";
import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";
import useIsAdmin from "../../hooks/useIsAdmin";

export const GET_EXERCISES = gql`
  query {
    allExercises {
      id
      name
      shortDescription
      category
      subcategory
      objectives
      taskDevelopment
    }
    currentUser {
      id
      role
    }
  }
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: "#fff",
    },
  })
);

// Main Container
const ExercisesContainer = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(2),
  minHeight: "60vh",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const FooterContainer = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  justifyContent: "flex-end",
  padding: theme.spacing(2),
  height: 90,
  width: "auto",
  right: 0,
  position: "fixed",
  bottom: 70,
  zIndex: 1200,
}));

// Card de ejercicio — superficie plana, un único acento
const ExerciseCard = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: "16px",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  marginBottom: theme.spacing(2),
  overflow: "hidden",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(16, 21, 26, 0.08)",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "14px",
    marginBottom: theme.spacing(1.5),
  },
}));

const ExerciseCardSelected = styled(ExerciseCard)(({ theme }: IStyledArguments) => ({
  borderColor: theme.palette.primary.main,
  borderWidth: 2,
  background: theme.palette.extra.accentSoft,
}));

const ExerciseHeader = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 2.5),
  cursor: "pointer",
  transition: "background 0.2s ease",
  "&:hover": {
    background: theme.palette.extra.backgroundGrey,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 2),
  },
}));

const ExerciseHeaderLeft = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  flex: 1,
}));

const ExerciseIcon = styled("div")(({ theme }: IStyledArguments) => ({
  width: 48,
  height: 48,
  borderRadius: "14px",
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    fontSize: "1.5rem",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    width: 40,
    height: 40,
    borderRadius: "12px",
    "& svg": {
      fontSize: "1.2rem",
    },
  },
}));

const ExerciseName = styled(Typography.Subtitle1)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: "1.1rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

const ExpandButton = styled(IconButton)(({ theme }: IStyledArguments) => ({
  color: theme.palette.primary.main,
  background: theme.palette.extra.accentSoft,
  transition: "background 0.2s ease",
  "&:hover": {
    background: theme.palette.extra.accentSoft,
  },
}));

const ExerciseContent = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(0, 2.5, 2.5, 2.5),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0, 2, 2, 2),
  },
}));

const Section = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "14px",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.extra.borderColor}`,
}));

const SectionHeader = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const SectionIcon = styled("div")(({ theme }: IStyledArguments) => ({
  width: 28,
  height: 28,
  borderRadius: "8px",
  background: theme.palette.extra.accentSoft,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    fontSize: "1rem",
    color: theme.palette.primary.main,
  },
}));

const SectionTitle = styled("div")(({ theme }: IStyledArguments) => ({
  color: theme.palette.primary.dark,
  fontWeight: 700,
  fontSize: "0.9rem",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
}));

const SectionText = styled(Typography.Subtitle2)(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
}));

const ActionButtons = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  flexWrap: "wrap",
}));

const DetailsButton = styled(Button)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  color: "#fff",
  fontWeight: 700,
  borderRadius: "12px",
  padding: theme.spacing(1, 3),
  textTransform: "none",
  transition: "background 0.2s ease",
  "&:hover": {
    background: theme.palette.primary.dark,
  },
}));

const AddToTrainingButton = styled(Button)(({ theme }: IStyledArguments) => ({
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.main,
  fontWeight: 700,
  borderRadius: "12px",
  padding: theme.spacing(1, 3),
  textTransform: "none",
  borderWidth: 2,
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: theme.palette.primary.dark,
    background: theme.palette.extra.accentSoft,
    borderWidth: 2,
  },
}));

const RemoveFromTrainingButton = styled(Button)(({ theme }: IStyledArguments) => ({
  background: theme.palette.error.main,
  color: "#fff",
  fontWeight: 700,
  borderRadius: "12px",
  padding: theme.spacing(1, 3),
  textTransform: "none",
  "&:hover": {
    background: theme.palette.error.main,
    opacity: 0.9,
  },
}));

// Estado vacío
const EmptyStateCard = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "20px",
  border: `2px dashed ${theme.palette.extra.borderColor}`,
  padding: theme.spacing(6),
  textAlign: "center",
  boxShadow: "none",
}));

const EmptyStateIcon = styled("div")(({ theme }: IStyledArguments) => ({
  width: 80,
  height: 80,
  borderRadius: "50%",
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
  marginBottom: theme.spacing(2),
  "& svg": {
    fontSize: "2.5rem",
    color: "#fff",
  },
}));

const EmptyStateTitle = styled(Typography.H5)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}));

const FloatingAddButton = styled(Fab)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  boxShadow: "0 6px 14px rgba(16, 21, 26, 0.25)",
  transition: "background 0.2s ease",
  width: 60,
  height: 60,
  "&:hover": {
    background: theme.palette.primary.dark,
  },
  "& svg": {
    fontSize: "1.6rem",
  },
}));

interface IExercises {
  isTraining?: boolean;
  callback?: (exercises: string[]) => void;
}

const ExercisesCollapse = injectIntl<IExercises>(
  ({ isTraining = false, callback, intl }) => {
    const classes = useStyles();

    const { data, loading } = useQuery<any>(GET_EXERCISES);
    const [allExercises, setAllExercises] = useState([]);
    const [selectedExercises, setSelectedExercises] = useState<string[]>([]);
    const [open, setOpen] = React.useState(false);
    const [expandedIds, setExpandedIds] = useState<string[]>([]);

    useEffect(() => {
      if (data && data.allExercises) {
        setAllExercises(data.allExercises);
      }
    }, [data]);

    useEffect(() => {
      if (loading) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    }, [loading]);

    useEffect(() => {
      if (selectedExercises) {
        callback && callback(selectedExercises);
      }
    }, [selectedExercises]);

    const handleAddExercise = (id: string) => {
      const newSelectedExercises = [...selectedExercises];
      newSelectedExercises.push(id);
      setSelectedExercises(newSelectedExercises);
    };

    const handleRemoveExercise = (id: string) => {
      const newSelectedExercises = [...selectedExercises];
      const index = newSelectedExercises.indexOf(id);
      if (index > -1) {
        newSelectedExercises.splice(index, 1);
      }
      setSelectedExercises(newSelectedExercises);
    };

    const toggleExpanded = (id: string) => {
      setExpandedIds((prev) =>
        prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
      );
    };

    const isExpanded = (id: string) => expandedIds.includes(id);

    const CardComponent = (exercise: any) =>
      selectedExercises.includes(exercise.id) ? ExerciseCardSelected : ExerciseCard;

    return (
      <ExercisesContainer>
        <Backdrop className={classes.backdrop} open={open}>
          <CircularProgress color="inherit" />
        </Backdrop>
        
        {!loading && (!allExercises || allExercises.length === 0) && (
          <EmptyStateCard elevation={0}>
            <EmptyStateIcon>
              <FitnessCenterIcon />
            </EmptyStateIcon>
            <EmptyStateTitle>{intl.formatMessage(messages.noResults)}</EmptyStateTitle>
          </EmptyStateCard>
        )}
        
        {allExercises &&
          allExercises.map((exercise: any) => {
            const CardEl = CardComponent(exercise);
            return (
              <CardEl key={exercise.id} elevation={0}>
                <ExerciseHeader onClick={() => toggleExpanded(exercise.id)}>
                  <ExerciseHeaderLeft>
                    <ExerciseIcon>
                      <FitnessCenterIcon />
                    </ExerciseIcon>
                    <ExerciseName>{exercise.name}</ExerciseName>
                  </ExerciseHeaderLeft>
                  <ExpandButton size="small">
                    {isExpanded(exercise.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ExpandButton>
                </ExerciseHeader>
                
                <Collapse in={isExpanded(exercise.id)}>
                  <ExerciseContent>
                    <Section>
                      <SectionHeader>
                        <SectionIcon>
                          <DescriptionIcon />
                        </SectionIcon>
                        <SectionTitle>
                          {intl.formatMessage(messages.description)}
                        </SectionTitle>
                      </SectionHeader>
                      <SectionText>{exercise.shortDescription}</SectionText>
                    </Section>
                    
                    <Section>
                      <SectionHeader>
                        <SectionIcon>
                          <AssignmentIcon />
                        </SectionIcon>
                        <SectionTitle>
                          {intl.formatMessage(messages.task)}
                        </SectionTitle>
                      </SectionHeader>
                      <SectionText>{exercise.taskDevelopment}</SectionText>
                    </Section>
                    
                    <ActionButtons>
                      {!isTraining && (
                        <ActionLink to={`/library/drills/${exercise.id}`}>
                          <DetailsButton variant="contained">
                            {intl.formatMessage(messages.details)}
                          </DetailsButton>
                        </ActionLink>
                      )}
                      
                      {isTraining && !selectedExercises.includes(exercise.id) && (
                        <AddToTrainingButton
                          variant="outlined"
                          fullWidth
                          onClick={() => handleAddExercise(exercise.id)}
                        >
                          {intl.formatMessage(messages.addToTraining)}
                        </AddToTrainingButton>
                      )}
                      
                      {isTraining && selectedExercises.includes(exercise.id) && (
                        <RemoveFromTrainingButton
                          variant="contained"
                          fullWidth
                          onClick={() => handleRemoveExercise(exercise.id)}
                        >
                          {intl.formatMessage(messages.removeFromTraining)}
                        </RemoveFromTrainingButton>
                      )}
                    </ActionButtons>
                  </ExerciseContent>
                </Collapse>
              </CardEl>
            );
          })}
        
        <FooterContainer>
          {!isTraining && useIsAdmin() && (
            <ActionLink to="/library/newdrill">
              <FloatingAddButton
                aria-label="add exercise"
                color="secondary"
              >
                <AddIcon />
              </FloatingAddButton>
            </ActionLink>
          )}
        </FooterContainer>
      </ExercisesContainer>
    );
  }
);

export default ExercisesCollapse;
